import moment from 'moment';
import get from 'lodash/get';
import { AUSTRALIAN_STATE } from 'Common/constants/australianState';
import { isAlphaNumeric } from 'Common/utilities/string';
import { getShortDatePattern } from 'Common/utilities/date';
import { toastError } from 'Common/utilities/alert';
import {
  addressToVM,
  getCurrentAddress,
  mapBasicAddress,
} from 'Common/utilities/address';
import {
  creditCheckVMToPostBody,
  clientToCreditCheckVM,
} from 'Common/utilities/creditChecks';
import { DRIVERS_LICENSE_DOCUMENT } from 'Common/constants/creditNumberType';
import { creditCheckAddress } from 'Common/mappers/creditCheck';
import { MAX_CURRENCY_LENGTH } from 'Common/constants/currency';

const DRIVERS_LICENCSE_VALIDATION = {
  MIN_LENGTH: 4,
  MAX_LENGTH: 9,
};

const CONTACT_TYPES = {
  EXISTING: 'existing',
  NON_MYCRM: 'nonMyCRM',
};

class SetCreditCheckModalCtrl {
  constructor(
    creditChecksService,
    generalService,
    loanToolsService,
    contactService,
    corporateService,
    commonFnService,
    configService,
    addressLookupService,
    optionsService,
  ) {
    'ngInject';

    this.creditChecksService = creditChecksService;
    this.generalService = generalService;
    this.loanToolsService = loanToolsService;
    this.contactService = contactService;
    this.corporateService = corporateService;
    this.commonFnService = commonFnService;
    this.configService = configService;
    this.addressLookupService = addressLookupService;
    this.optionsService = optionsService;
  }

  $onInit() {
    this.isAddressOverhaulEnabled = false;
    this.address = {};
    this.familyId =
      (this.selectedContact && this.selectedContact.FamilyID) || 0;
    this.states = AUSTRALIAN_STATE;
    this.contactTypes = CONTACT_TYPES;
    this.minAge = 15;
    this.isPrivacyConfirm = false;

    this.getShortDatePattern = getShortDatePattern;
    this.showCreditCheckEquifaxDetails = this.configService.feature.creditCheckEquifaxDetails;

    this.creditCheck = { contactType: CONTACT_TYPES.EXISTING };
    this.contactType = CONTACT_TYPES.EXISTING;
    this.MAX_CURRENCY_LENGTH = MAX_CURRENCY_LENGTH;

    this.hasClientsList = this.clientsList?.length;

    this.initDatePicker();
    this.validateDOB();
    this.validateDriversLicense();
    this.getAccountTypeList();
  }

  handleUpdateAddress({ address }) {
    this.address = address;
  }

  initDatePicker() {
    const maxDOB = moment().subtract(this.minAge, 'years').toDate();
    this.dateSettings = {
      altInputFormats: ['d!/M!/yyyy', 'd!M!yyyy'],
      initDate: maxDOB,
      maxDate: maxDOB,
    };
  }

  getAccountTypeList() {
    this.optionsService.getEquifaxAccountTypes().then((response) => {
      this.accountTypesList = response;
    });
  }

  onContactSelectorInitialized(api) {
    this.contactSelector = api;
    if (this.selectedContact) {
      this.contactSelector.setSelectedContact(this.selectedContact);
    }
  }

  getAdvisersDetailsForCreditCheck() {
    if (!this.selectedContact) {
      return;
    }
    const { isAdviser, FamilyID: familyId } = this.selectedContact;
    if (!isAdviser || !familyId) {
      return;
    }
    this.corporateService.familyAddressGet(familyId).then(({ data }) => {
      if (!data || !data.length) {
        return {};
      }
      const addressTmp = this.commonFnService.setAddressTypes(data);
      const {
        route,
        streetAddress: streetName,
        streetNumber,
        locality: suburb,
        stateShortName: state,
        postCode,
      } = addressToVM(addressTmp.home);
      this.creditCheck = {
        ...this.creditCheck,
        streetNumber,
        suburb,
        state,
        postCode,
        route,
        streetName,
      };
    });
  }

  setSelectedClientAddress(client) {
    if (!client) {
      return;
    }

    if (this.hasClientsList) {
      this.familyId = client.FamilyId;
      this.contactService
        .clientInformGet(client.FamilyId, client.PersonId, true)
        .then(({ data }) => {
          const address = get(data, '0.Address');
          this.creditCheck = {
            ...this.creditCheck,
            Address: address,
          };

          const currentAddress = getCurrentAddress(address);
          this.onAddressSelected(currentAddress, true);
        });
      return;
    }

    if (client.Address && client.Address.length) {
      const currentAddress = getCurrentAddress(client.Address);
      this.onAddressSelected(currentAddress, true);
    } else {
      this.clearAddress();
    }
  }

  onClientSelected(client) {
    this.creditCheck.driversLicense = '';
    if (!client) {
      return;
    }

    this.selectedClient = client;
    this.creditCheck = {
      ...this.creditCheck,
      ...clientToCreditCheckVM(client),
    };
    this.setDOB(this.creditCheck.dateOfBirth);
    this.validateDOB();

    this.setSelectedClientAddress(client);

    this.contactService
      .clientEntityIdentificationGet(
        client.FamilyId,
        null,
        DRIVERS_LICENSE_DOCUMENT.ID,
      )
      .then(({ data }) => {
        if (!data) {
          return;
        }
        if (data.length && this.creditCheck) {
          const empFiltered = data.filter((emp) => {
            return parseInt(client.PersonId, 10) === emp.ClientEntityId;
          });
          if (empFiltered.length) {
            this.creditCheck.driversLicense = empFiltered[0].DocumentNumber;
          }
        }
      });
    this.getAdvisersDetailsForCreditCheck();
  }

  clearAddress() {
    const {
      route,
      streetAddress,
      streetNumber,
      suburb,
      state,
      postCode,
      streetName,
      ...cleanCreditCheck
    } = this.creditCheck;
    this.creditCheck = cleanCreditCheck;
  }

  onAddressSelected(address, isClient = false) {
    if (isClient) {
      if (address) {
        this.creditCheck = {
          ...this.creditCheck,
          ...mapBasicAddress(address),
        };
      } else {
        this.clearAddress();
      }
      this.searchAddressShown = false;
      return;
    }
    if (address && address.formatted_address) {
      this.generalService
        .geocodeSearch(address.formatted_address)
        .then((response) => {
          if (!response || !response.data) {
            return;
          }

          const {
            route,
            streetAddress,
            streetNumber,
            locality: suburb,
            stateShortName: state,
            postCode,
          } = addressToVM(response.data);
          this.creditCheck = {
            ...this.creditCheck,
            streetNumber,
            suburb,
            state,
            postCode,
          };
          this.creditCheck.streetName = streetAddress || route;
          this.searchAddressShown = false;
        });
    }
  }

  toggleDatePicker() {
    this.dateIsOpen = !this.dateIsOpen;
  }

  toggleSearchAddress() {
    this.searchAddressShown = !this.searchAddressShown;
  }

  setDOB(dob) {
    if (dob) {
      this.dob = moment(dob).toDate();
    }
  }

  setContactType(type) {
    if (this.creditCheck.contactType === type) {
      return;
    }

    const currentCreditCheck = { ...this.creditCheck };
    if (this.prevCreditCheck) {
      const { clientId, firstName, lastName } = this.prevCreditCheck;
      this.creditCheck = { ...this.creditCheck, clientId, firstName, lastName };
    } else {
      if (type === CONTACT_TYPES.NON_MYCRM) {
        this.creditCheck.clientId = 0;
      }
      this.creditCheck.firstName = null;
      this.creditCheck.lastName = null;
    }
    this.prevCreditCheck = currentCreditCheck;
    this.creditCheck.contactType = type;
  }

  isValidCreditCheck(form) {
    if (!this.hasSelectedClient()) {
      return false;
    }

    const isValidGender = !!this.creditCheck.gender;
    return (
      isValidGender &&
      this.isValidDOB &&
      this.isValidAddress(form) &&
      this.isValidDriversLicense
    );
  }

  isValidAddress(form) {
    if (this.isAddressOverhaulEnabled) {
      return form.$valid;
    }
    const { streetName, suburb } = this.creditCheck;
    const isValidStreetName = streetName && streetName.trim();
    const isValidSuburb = suburb && suburb.trim();
    return isValidStreetName && isValidSuburb;
  }

  hasSelectedClient() {
    let hasSelectedClient;
    if (this.creditCheck.contactType === CONTACT_TYPES.EXISTING) {
      const isValidClientId = this.creditCheck.clientId;
      hasSelectedClient = isValidClientId;
    } else {
      const { firstName, lastName } = this.creditCheck;
      const isValidFirstName = firstName && firstName.trim();
      const isValidLastName = lastName && lastName.trim();
      hasSelectedClient = isValidFirstName && isValidLastName;
    }
    return hasSelectedClient;
  }

  validateDOB() {
    const date = this.dob && moment(this.dob);
    const isValidDate = date && date.isValid();
    if (!isValidDate) {
      this.isValidDOB = false;
      return;
    }

    const contactAge = moment().diff(date, 'year');
    this.isValidDOB = contactAge >= this.minAge;
  }

  validateDriversLicense() {
    const { driversLicense } = this.creditCheck;
    const isEmptyDriversLicense = !driversLicense;
    const isValidFormat = isAlphaNumeric(
      driversLicense,
      DRIVERS_LICENCSE_VALIDATION.MIN_LENGTH,
      DRIVERS_LICENCSE_VALIDATION.MAX_LENGTH,
    );
    this.isValidDriversLicense = isEmptyDriversLicense || isValidFormat;
  }

  saveAddress() {
    return this.addressLookupService
      .convertToAddressFormat(this.address)
      .then((address) => {
        this.address = address;
        return this.generalService.addressDetailsSet(this.address);
      })
      .then(({ data: addressId }) => {
        this.creditCheck = {
          ...this.creditCheck,
          ...creditCheckAddress(this.address),
          addressId,
        };
        return this.creditCheck;
      });
  }

  submitCreditCheck(form) {
    if (!this.isValidCreditCheck(form)) {
      toastError('Please fill up the form properly');
      return;
    }
    this.isSubmitting = true;
    if (!this.isAddressOverhaulEnabled) {
      return this.save();
    }
    this.saveAddress().then(() => {
      this.save();
    });
  }

  save() {
    this.creditCheck.dateOfBirth = this.getFormattedDOB();
    this.creditChecksService
      .setCreditChecks(creditCheckVMToPostBody(this.creditCheck))
      .then(
        (response) => {
          this.isSubmitting = false;
          this.modalInstance.close(response.data);
        },
        () => {
          this.isSubmitting = false;
        },
      );
  }

  getFormattedDOB() {
    const momentDOB = moment(this.dob);
    return `${momentDOB.format('YYYY-MM-DDT00:00:00.000')}Z`;
  }

  close() {
    this.modalInstance.dismiss();
  }
}

export default SetCreditCheckModalCtrl;
